import {
  FC,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import ReactDataSheet from 'react-datasheet'

import { Button } from '@progress/kendo-react-buttons'

import 'react-datasheet/lib/react-datasheet.css'
import { ConfirmReplyActions, useAppDispatch, useAppSelector } from '../../../stores'
import { useNavigate } from 'react-router-dom'
import { ErrorMessage } from '../../../components/parts'
import { MessageProvider } from '../../../services'
import { forwardGuard } from '../../../components/hooks'
import { CodeName } from '../../../services/initializing/CodeName'
import hearingMessage from '../../../data/hearing-message.json'
import { Authority } from '../../../gateway/system/Authority'

export interface CellData extends ReactDataSheet.Cell<CellData, number> {
  value: number | null
}

class DataSheet extends ReactDataSheet<CellData, number> {}

const months = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]

// メインのグリッド設定
const SheetRenderer: FC<PropsWithChildren<{
  className: string
  simulationYm: string
  displayStartYear: string
}>> = (props) => {
  const startYear = Number(props.displayStartYear)

  const years = Array.from({ length: 6 }, (_, i) => startYear + i)

  const ref = useRef<HTMLDivElement>(null)
  const location = useAppSelector((state) => state.confirmreply.location.confirmReplyLocation)
  const dispatch = useAppDispatch()

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location })
  }, [location])

  return (
    <div
      ref={ref}
      className="scrollable-container"
      onScroll={(event) => {
        dispatch(ConfirmReplyActions.setConfirmReplyLocation(Number(ref.current?.scrollLeft)))
      }} >
      <table
        className={props.className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 70 }}>回答</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 73 }}>
              <div style={{ width: 110 }}>ヒアリング項目</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 186 }}>
              <div style={{ width: 55 }}>登録状況</div>
            </th>
            {years.map((year) => (
              <th key={year} colSpan={12}>{year}年度</th>
            ))}
          </tr>
          <tr>
            {years.flatMap(year => months.map(month => ({ year, month }))).map(({ year, month }, index) => (
              <th key={`${year}-${month}-${index}`} className="calendar-month">
                {month}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{props.children}</tbody>
      </table>
    </div>
  )
}

// 左ヘッダの設定
const RowRenderer: FC<PropsWithChildren<{
  row: number
  rowLength: number
  setNotificationMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  setErrorMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  isChanged: boolean
}>> = (props) => {
  const { regStatus, replyStsCd } = useAppSelector((state) => state.confirmreply)
  const userProp = useAppSelector((state) => state.common.userProp)
  const navigate = useNavigate()
  const button = (row: number) => {
    switch (row) {
      case 0:
      case 1:
        return (
          <Button
            onClick={() => {
              forwardGuard('内容が編集中です。このページを離れますか？', navigate, '/supplier/building-decided', props.isChanged)
            }}
          >
            詳細
          </Button>
        )
      case 2:
      case 3:
      case 4:
        return (<></>)
      case 5:
        return (
          <Button
            onClick={() => {
              forwardGuard('内容が編集中です。このページを離れますか？', navigate, '/supplier/other-companies-building', props.isChanged)
            }}
          >
            詳細
          </Button>
        )
      case 6:
      case 7:
      case 8:
      case 9:
        return (
          <Button
            onClick={() => {
              if (
                replyStsCd !== '8' &&
                (
                  regStatus.addStsCdSupplierDecisionBuilding !== '3' ||
                  regStatus.addStsCdSupplierEstimationBuilding !== '3' ||
                  regStatus.addStsCdOtherCompanyBuilding !== '3'
                )
              ) {
                const checkMessage: string[] = []
                if (regStatus.addStsCdSupplierDecisionBuilding !== '3') {
                  checkMessage.push('大成決定物件')
                }
                if (regStatus.addStsCdSupplierEstimationBuilding !== '3') {
                  checkMessage.push('大成見積中物件')
                }
                if (regStatus.addStsCdOtherCompanyBuilding !== '3') {
                  checkMessage.push('他社物件')
                }

                const message: ErrorMessage[] = [{
                  id: 'E017',
                  content: MessageProvider.provide(
                    'E017', checkMessage.join(',')
                  )
                }]
                props.setErrorMessages(message)
                return
              }

              forwardGuard('内容が編集中です。このページを離れますか？', navigate, '/supplier/sufficiency-of-quantity', props.isChanged)
            }}
          >
            詳細
          </Button>
        )
      case 10:
      case 11:
      case 12:
        return (
          <Button
            onClick={() => {
              forwardGuard('内容が編集中です。このページを離れますか？', navigate, '/supplier/hearing-sheet', props.isChanged)
            }}
          >
            詳細
          </Button>
        )
    }
  }

  const label = (row: number) => {
    switch (row) {
      case 0:
        return '大成決定物件'
      case 1:
        return '大成見積中物件'
      case 2:
        return '竣工済物件'
      case 3:
        return '非公開決定物件'
      case 4:
        return '非公開未決定物件'
      case 5:
        return '他社物件'
      case 6:
        return '合計（大成物件）'
      case 7:
        return '合計（大成物件＋他社物件）'
      case 8:
        return '貴社最大能力'
      case 9:
        return '大成対応可能数'
      case 10:
        return '過不足状況'
      case 11:
        return '使用率'
      case 12:
        return 'ヒアリングシート'
      default:
        return ''
    }
  }

  const regShowLabel = (regCode: string) => {
    if (regCode === '1') {
      return <span style={{ color: '#D0611E' }}>未登録</span>
    } else if (regCode === '2') {
      return <span>一時保存</span>
    } else if (regCode === '3') {
      return <span style={{ color: '#00A27C' }}>登録済</span>
    } else if (regCode === '4') {
      return <span style={{ color: '#00307C' }}>未登録</span>
    } else {
      return <span></span>
    }
  }

  const regStatusEvent = (row: number) => {
    switch (row) {
      case 0:
        return regShowLabel(regStatus.addStsCdSupplierDecisionBuilding)
      case 1:
        return regShowLabel(regStatus.addStsCdSupplierEstimationBuilding)
      case 2:
      case 3:
      case 4:
        return (<></>)
      case 5:
        return regShowLabel(regStatus.addStsCdOtherCompanyBuilding)
      case 6:
      case 7:
      case 8:
        return regShowLabel(regStatus.addStsCdSupplierMaxCapacity)
      case 9:
        return regShowLabel(regStatus.addStsCdSupplierMaxCapacity)
      case 10:
      case 11:
      case 12:
        return regShowLabel(regStatus.addStsCdHearingSheet)
      default:
        return ''
    }
  }

  const regShowBackGround = (row: number) => {
    let regCode
    switch (row) {
      case 0:
        regCode = regStatus.addStsCdSupplierDecisionBuilding
        break
      case 1:
        regCode = regStatus.addStsCdSupplierEstimationBuilding
        break
      case 2:
      case 3:
      case 4:
      case 5:
        regCode = regStatus.addStsCdOtherCompanyBuilding
        break
      case 6:
      case 7:
      case 8:
        regCode = regStatus.addStsCdSupplierMaxCapacity
        break
      case 9:
        regCode = regStatus.addStsCdSupplierMaxCapacity
        break
      case 10:
      case 11:
      case 12:
        regCode = regStatus.addStsCdHearingSheet
        break
      default:
        regCode = ''
    }

    if (regCode === '4') {
      return '#F0E68C'
    } else {
      return ''
    }
  }

  if (props.row === 12) {
    // ヒアリングシート行
    return (
      <tr style={{ height: 33 }}>
        <th style={{ position: 'sticky', left: 0 }}>{button(props.row)}</th>
        <th style={{ position: 'sticky', left: 73 }}>
          <span>{label(props.row)}</span>
        </th>
        <th style={{ position: 'sticky', left: 186, backgroundColor: regShowBackGround(props.row) }}>
          {regStatusEvent(props.row)}
        </th>
        <th colSpan={72}></th>
      </tr>
    )
  } else if (props.row === 2) {
    // 竣工済物件行
    return (
      <>
        <tr style={{ height: 33 }}>
          <th style={{ position: 'sticky', left: 0 }}>{button(props.row)}</th>
          <th style={{ position: 'sticky', left: 73 }}>
          <span>{label(props.row)}</span>
          </th>
          <th style={{ position: 'sticky', left: 186 }}></th>
          {props.children}
        </tr>
      </>
    )
  } else if (props.row === 3 || props.row === 4) {
    // 非公開物件行
    if (userProp?.authority === Authority.Supplier) {
      return (<></>)
    } else {
      return (
        <>
          <tr style={{ height: 33 }}>
            <th style={{ position: 'sticky', left: 0, backgroundColor: '#FFFFFF' }}>{button(props.row)}</th>
            <th style={{ position: 'sticky', left: 73, backgroundColor: '#FFFFFF' }}>
            <span title={hearingMessage.confirmReply.unpublishedBuildingTooltip}>{label(props.row)}</span>
            </th>
            <th style={{ position: 'sticky', left: 186, backgroundColor: '#FFFFFF' }}>
              {regStatusEvent(props.row)}
            </th>
            {props.children}
          </tr>
        </>
      )
    }
  } else if (props.row !== 6 && props.row !== 7 && props.row !== 10 && props.row !== 11) {
    // ヒアリング項目行
    return (
      <>
        <tr style={{ height: 33 }}>
          {
          props.row === 0
            ? (
                <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
                  {button(props.row)}
                </th>
              )
            : props.row === 1
              ? (
                  <></>
                )
              : props.row === 8
                ? (
                    <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
                      {button(props.row)}
                    </th>
                  )
                : props.row === 9
                  ? (
                      <></>
                    )
                  : (
            <th style={{ position: 'sticky', left: 0 }}>{button(props.row)}</th>
                    )}
          <th style={{ position: 'sticky', left: 73 }}>
            <span>{label(props.row)}</span>
          </th>
          <th style={{ position: 'sticky', left: 186, backgroundColor: regShowBackGround(props.row) }}>
            {regStatusEvent(props.row)}
          </th>
          {props.children}
        </tr>
      </>
    )
  } else {
    return (
      // 合計行
      <tr style={{ height: 33 }}>
        <th
          style={{ position: 'sticky', left: 0, textAlign: 'left' }}
          colSpan={3}
        >
          <span>{label(props.row)}</span>
        </th>
        {props.children}
      </tr>
    )
  }
}

// セルの設定
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<CellData, number>
}> = (props) => {
  const bg =
    props.valueViewerProps.row === 6 ||
    props.valueViewerProps.row === 7 ||
    props.valueViewerProps.row === 10 ||
    props.valueViewerProps.row === 11
      ? '#99FF99'
      : props.valueViewerProps.row === 3 ||
        props.valueViewerProps.row === 4
        ? '#FFFFFF'
        : ''
  return (
    <div style={{ backgroundColor: bg, height: '100%' }}>
      {props.valueViewerProps.value}
    </div>
  )
}

// レンダリング
export const ConfirmReplyGrid: FC<{
  data: CellData[][]
  hearingSheetFlg: number
  simulationYm: string
  setNotificationMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  setErrorMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  isChanged: boolean
  expenseCd: string
}> = (props) => {
  const [data, setData] = useState<CellData[][]>([])
  const initialData = useAppSelector((x) => x.common.initialData)
  const { displayStartYear } = useAppSelector((state) => state.common)
  const expenses = initialData.codeNames('HI01')
  const unit = expenses.find((item: CodeName) => {
    return item.CODE === props.expenseCd
  })?.TXT8

  useEffect(() => {
    if (props.hearingSheetFlg === 1) {
      setData([...props.data, []])
    } else {
      setData(props.data)
    }
  }, [props.data, props.hearingSheetFlg])

  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<CellData, number>
  ) => {
    return (
      <SheetRenderer
        className={sheetRendererProps.className}
        simulationYm={props.simulationYm}
        displayStartYear={displayStartYear.toString()}
      >
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<CellData, number>
  ) => {
    return (
      <RowRenderer
        row={rowRendererProps.row}
        rowLength={data.length}
        setNotificationMessages={props.setNotificationMessages}
        setErrorMessages={props.setErrorMessages}
        isChanged={props.isChanged}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<CellData, number>
  ) => {
    return <ValueViewer valueViewerProps={valueViewerProps} />
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <h3 style={{ marginBottom: 0 }}>
          （単位：{unit}）
          <span
            style={{ marginLeft: '16px', color: '#d51923', fontWeight: '700', fontSize: '16px' }}
          >
            {hearingMessage.confirmReply.confirmReplyGridAnnotationInfo}
          </span>
        </h3>
      </div>
      <DataSheet
        data={data}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
      />
    </div>
  )
}
